import api from './api';

// ── Types ────────────────────────────────────────────────────────────────────

export interface Embodiment {
  number: number;
  characterizing_feature: string;
  figure_ids: string[];
  claim_numbers: number[];
  needs_additional_figures: boolean;
  missing_figure_descriptions: string[];
  note: string | null;
}

export interface ReferenceSign {
  sign: string;
  component: string;
  figure_ids: string[];
}

export interface ClaimSupportFlag {
  claim_number: number;
  feature_text: string;
  supported: boolean;
  supporting_paragraph: string | null;
  note: string | null;
}

export interface StepStatuses {
  step1_inputs: string;
  step2_structure: string;
  step3_reference_signs: string;
  step4_approval: string;
  step5_description: string;
  step6_title_abstract: string;
  step7_coherence: string;
}

export interface PatentDraft {
  id: string;
  solution_id: string;
  sub_project_id: string;
  project_id: string;
  client_id: string;
  title: string | null;
  abstract: string | null;
  description_html: string;
  claims_section_html: string;
  embodiments: Embodiment[];
  reference_signs: ReferenceSign[];
  support_audit: ClaimSupportFlag[];
  step_statuses: StepStatuses;
  version: number;
  created_at: string;
  last_modified_at: string;
  last_modified_by: string | null;
}

export interface DPACostEstimate {
  num_figures: number;
  num_claims: number;
  num_embodiments_estimated: number;
  estimated_input_tokens: number;
  estimated_output_tokens: number;
  estimated_cost_cents: number;
  currency: string;
  model_id: string;
  model_name: string | null;
}

export interface StepResult {
  status: string;
  content?: string;
  error?: string;
  claim_support_audit?: string;
  figure_ids?: string[];
}

// ── Service ──────────────────────────────────────────────────────────────────

export const patentDraftService = {
  async getDraft(subProjectId: string, solutionId: string): Promise<PatentDraft> {
    const res = await api.get(`/patent-draft/${subProjectId}/${solutionId}`);
    return res.data;
  },

  async estimateCost(subProjectId: string, solutionId: string): Promise<DPACostEstimate> {
    const res = await api.post('/patent-draft/estimate', {
      sub_project_id: subProjectId,
      solution_id: solutionId,
    });
    return res.data;
  },

  async runStep(subProjectId: string, solutionId: string, step: string): Promise<StepResult> {
    const res = await api.post('/patent-draft/run-step', {
      sub_project_id: subProjectId,
      solution_id: solutionId,
      step,
    });
    return res.data;
  },

  async updateStructure(
    subProjectId: string,
    solutionId: string,
    embodiments: Embodiment[],
    referenceSigns: ReferenceSign[],
    version: number,
  ): Promise<PatentDraft> {
    const res = await api.put('/patent-draft/structure', {
      sub_project_id: subProjectId,
      solution_id: solutionId,
      embodiments,
      reference_signs: referenceSigns,
      version,
    });
    return res.data;
  },

  async updateContent(
    subProjectId: string,
    solutionId: string,
    version: number,
    updates: {
      title?: string;
      abstract?: string;
      description_html?: string;
      claims_section_html?: string;
    },
  ): Promise<PatentDraft> {
    const res = await api.put('/patent-draft/content', {
      sub_project_id: subProjectId,
      solution_id: solutionId,
      version,
      ...updates,
    });
    return res.data;
  },

  async deleteDraft(subProjectId: string, solutionId: string): Promise<void> {
    await api.delete(`/patent-draft/${subProjectId}/${solutionId}`);
  },

  getDescriptionExportUrl(subProjectId: string, solutionId: string): string {
    return `/patent-draft/${subProjectId}/${solutionId}/export/description`;
  },

  getFiguresExportUrl(subProjectId: string, solutionId: string): string {
    return `/patent-draft/${subProjectId}/${solutionId}/export/figures`;
  },

  async downloadDescription(subProjectId: string, solutionId: string): Promise<Blob> {
    const res = await api.get(
      `/patent-draft/${subProjectId}/${solutionId}/export/description`,
      { responseType: 'blob' },
    );
    return res.data;
  },

  async downloadFigures(subProjectId: string, solutionId: string): Promise<Blob> {
    const res = await api.get(
      `/patent-draft/${subProjectId}/${solutionId}/export/figures`,
      { responseType: 'blob' },
    );
    return res.data;
  },
};
